/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Condue  ®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */

import moment from 'moment';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const blueColorWithSelectedIcon = `<svg id="Checkbox-selected" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
<rect id="Rectangle_2336" data-name="Rectangle 2336" width="20" height="20" rx="6" fill="#347dc6"/>
<path id="Path_18" data-name="Path 18" d="M4.4,7.3,0,2.908l1.42-1.42L4.4,4.4,8.792,0l1.42,1.42Z" transform="translate(4.734 6.763)" fill="#fff"/>
</svg>`;

const deselectedIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
<g id="Checkbox" fill="#fff" stroke="#aaafb9" stroke-width="1">
  <rect width="20" height="20" rx="6" stroke="none"/>
  <rect x="0.5" y="0.5" width="19" height="19" rx="5.5" fill="none"/>
</g>
</svg>`;

const contentFont = 10;
const pageMargins = [30.8, 10, 30.8, 25.92];
const styles = {
  header: {
    fontSize: 18,
    bold: true,
    margin: [0, 0, 0, 10],
  },
  subheader: {
    fontSize: 16,
    bold: true,
    margin: [0, 10, 0, 5],
  },
  tableExample: {
    margin: [0, 5, 0, 15],
  },
  tableHeader: {
    bold: true,
    fontSize: 13,
    color: 'black',
  },
};

const valueFormatter = (value = 0) => {
  if (value === '' || value === null || value === undefined || isNaN(parseFloat(value))) {
    return '';
  }
  
  const numValue = parseFloat(value);
  if (numValue < 0) {
    return `($${numValue
      .toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
      .substring(2)})`;
  }
  return numValue.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
};

const numberFormatter = (value = 0) => {
  if (value === '' || value === null || value === undefined || isNaN(parseFloat(value))) {
    return '';
  }
  
  const numValue = parseFloat(value);
  if (numValue < 0) {
    return `(${numValue.toLocaleString('en-US').substring(1)})`;
  }
  return numValue.toLocaleString('en-US', {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
};

const getFooter = (data) => {
  const { selectedCriteria } = data;

  return {
    alignment: 'center',
    fontSize: 9,
    margin: [28.8, 10, 28.8, 20],
    style: 'tableExample',
    table: {
      widths: ['*', '*', '*', '*', '*', '*'],
      body: [
        [
          {
            text: [{ text: 'Invoiced: ', bold: true }, selectedCriteria?.invoiced || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Not Invoiced: ', bold: true }, selectedCriteria?.notInvoiced || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Billed Units: ', bold: true }, selectedCriteria?.billedUnits || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Not Billed Units: ', bold: true }, selectedCriteria?.notBilledUnits || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'For Dispute Resolution: ', bold: true }, selectedCriteria?.forDisputeResolution || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Not For Dispute Resolution: ', bold: true }, selectedCriteria?.notForDisputeResolution || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
        ],
      ],
    },
    layout: 'noBorders',
  };
};

const getSelectionCriteriaHeader = (data) => {
  const { selectedCriteria } = data;

  return {
    alignment: 'center',
    fontSize: 9,
    margin: [30.8, 5, 30.8, 10],
    style: 'tableExample',
    table: {
      widths: ['*','*', '*', '*', '*', '*', '*'],
      body: [
        [{
          text: [{ text: 'Selection Criteria', bold: true  }, ''],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
        },
          {
            text: [{ text: 'Invoiced: ' }, selectedCriteria?.invoiced || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Not Invoiced: '}, selectedCriteria?.notInvoiced || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Billed Units: ' }, selectedCriteria?.billedUnits || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Not Billed Units: '}, selectedCriteria?.notBilledUnits || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'For Dispute Resolution: ' }, selectedCriteria?.forDisputeResolution || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
          {
            text: [{ text: 'Not For Dispute Resolution: ' }, selectedCriteria?.notForDisputeResolution || 'N/A'],
            fontSize: 9,
            fontFamily: 'arial',
            alignment: 'center',
          },
        ],
      ],
    },
    layout: 'noBorders',
  };
};

const getHeaderColumns = (columns) => {
  const border = [true, true, true, true];
  const headers = [];

  columns.forEach((col) => {
    if (col.field === 'ccCommentsExist') {
      return;
    }

    headers.push({
      text: col.headerName || col,
      fontSize: contentFont,
      alignment: 'center',
      color: 'black',
      bold: true,
      border,
      width: '*',
    });
  });

  return headers;
};

const getTableRows = (gridData, columnDefs) => {
  const rows = [];

  if (!gridData || gridData.length === 0) {
    return rows;
  }

  gridData.forEach((item, index) => {
    const row = [];

    columnDefs.forEach((col) => {
      if (col.field === 'ccCommentsExist') {
        return;
      }

      let cellValue = item[col.field];
      
      if (cellValue === undefined || cellValue === null) {
        cellValue = '';
      }

      if (col.field && (col.field.includes('Amt') || col.field === 'submittedAmt' || col.field === 'allowedAmt' || col.field === 'reimbursementAmt')) {
        cellValue = valueFormatter(cellValue);
      } else if (col.field && (col.field.includes('Units') || col.field.includes('Unit'))) {
        cellValue = numberFormatter(cellValue);
      } else if (col.field === 'invoicedInd' || col.field === 'reversalInd') {
        const isChecked = cellValue === 1 || cellValue === '1' || cellValue === true;
        row.push({
          alignment: 'center',
          border: [true, true, true, true],
          width: 14,
          height: 14,
          marginTop: -1,
          svg: isChecked ? blueColorWithSelectedIcon : deselectedIcon,
        });
        return;
      }

      row.push({
        text: cellValue,
        fontSize: contentFont,
        alignment: col.align || 'center',
        border: [true, true, true, true],
      });
    });

    rows.push(row);
  });

  return rows;
};

const getGrandTotalRow = (gridData, columnDefs) => {
  const row = [];
  
  if (!gridData || gridData.length === 0) {
    return row;
  }

  let totalSubmitted = 0;
  let totalAllowed = 0;
  let totalReimb = 0;
  let totalOrigUnits = 0;
  let totalNumUnits = 0;
  let totalBilledUnits = 0;
  let firstCellAdded = false;

  gridData.forEach((item) => {
    columnDefs.forEach((col) => {
      if (col.field === 'ccCommentsExist') {
        return;
      }

      const cellValue = item[col.field];
      
      if (cellValue === undefined || cellValue === null || cellValue === '') {
        return;
      }

      const numValue = parseFloat(String(cellValue).replace(/[^0-9.-]/g, '')) || 0;

      if (col.field === 'submittedAmt') {
        totalSubmitted += numValue;
      } else if (col.field === 'allowedAmt') {
        totalAllowed += numValue;
      } else if (col.field === 'reimbursementAmt') {
        totalReimb += numValue;
      } else if (col.field === 'origNumOfUnits') {
        totalOrigUnits += numValue;
      } else if (col.field === 'numOfUnits') {
        totalNumUnits += numValue;
      } else if (col.field === 'billedUnits') {
        totalBilledUnits += numValue;
      }
    });
  });

  columnDefs.forEach((col) => {
    if (col.field === 'ccCommentsExist') {
      return;
    }

    let cellValue = '';
    
    if (!firstCellAdded) {
      cellValue = `Grand Total: ${gridData.length} claims`;
      firstCellAdded = true;
    } else if (col.field === 'submittedAmt') {
      cellValue = valueFormatter(totalSubmitted);
    } else if (col.field === 'allowedAmt') {
      cellValue = valueFormatter(totalAllowed);
    } else if (col.field === 'reimbursementAmt') {
      cellValue = valueFormatter(totalReimb);
    } else if (col.field === 'origNumOfUnits') {
      cellValue = numberFormatter(totalOrigUnits);
    } else if (col.field === 'numOfUnits') {
      cellValue = numberFormatter(totalNumUnits);
    } else if (col.field === 'billedUnits') {
      cellValue = numberFormatter(totalBilledUnits);
    }

    row.push({
      text: cellValue,
      fontSize: contentFont,
      bold: true,
      alignment: col.align || 'center',
      border: [true, true, true, true],
    });
  });

  return row;
};

const getColumnWidths = (columnDefs) => {
  const widths = [];
  
  columnDefs.forEach((col) => {
    if (col.field === 'ccCommentsExist') {
      return;
    }
    
    if (col.field === 'claimId') {
      widths.push(100);
    }else if(col.field ==='claimLineNum'){
widths.push(80);
    }else if(col.field ==='invoicedInd'){
widths.push(80);
    } else if(col.field ==='reversalInd'){
widths.push(80);
    } else if(col.field ==='claimCategoryCd'){
widths.push(50);
    } else if(col.field ==='numOfUnits'){
widths.push(70);
    }else if(col.field ==='origNumOfUnits'){
widths.push(70);
    }else if(col.field ==='billedUnits'){
widths.push(70);
    }
    else if(col.field ==='providerIdTypeCd'){
widths.push(70);
    }
     else {
      widths.push('*');
    }
  });
  
  return widths;
};

const exportAdjustedClaimsPDF = (
  groupedByDrugSeq,
  unitColumnDefs,
  adjustedClaimsColumnDefs,
  options,
  logInUser
) => {
  const prinDate = moment().format('MM/DD/YYYY');

  const sortedDrugSeqs = Object.keys(groupedByDrugSeq).sort((a, b) => {
    const ndcA = groupedByDrugSeq[a].headerInfo?.ndc || '';
    const ndcB = groupedByDrugSeq[b].headerInfo?.ndc || '';
    return String(ndcA).localeCompare(String(ndcB));
  });

  const allContent = [];
  
  let firstNdc = '';
  let firstDrugName = '';

  const createHeaderSection = (ndc, drugName, currentPage, pageCount) => {
    return {
      alignment: 'center',
      fontSize: 10,
      margin: [30.8, 0, 30.8, 10],
      table: {
        widths: ['*', '*', '*'],
        body: [
          [
            {
              text: [{ text: 'Requested By: ', bold: true }, logInUser || 'System'],
              fontSize: 10,
              fontFamily: 'arial',
              alignment: 'left',
              border: [false, false, false, false],
            },
            {
              text: 'Adjusted Claims Report',
              fontSize: 12,
              bold: true,
              fontFamily: 'arial',
              alignment: 'center',
              border: [false, false, false, false],
            },
            {
              text: [{ text: 'Date Printed: ', bold: true }, moment().format('MM/DD/YYYY')],
              fontSize: 10,
              fontFamily: 'arial',
              alignment: 'right',
              border: [false, false, false, false],
            },
          ],
          [
            {
              text: [{ text: 'NDC: ', bold: true }, ndc],
              fontSize: 10,
              fontFamily: 'arial',
              alignment: 'left',
              border: [false, false, false, false],
            },
            {
              text: options.rebateProgramName || '',
              fontSize: 10,
              fontFamily: 'arial',
              alignment: 'center',
              bold: true,
              border: [false, false, false, false],
            },
            {
              text: `Page ${currentPage} of ${pageCount}`,
              fontSize: 10,
              fontFamily: 'arial',
              alignment: 'right',
              border: [false, false, false, false],
            },
          ],
          [
            {
              text: [{ text: 'Drug Name: ', bold: true }, drugName],
              fontSize: 10,
              fontFamily: 'arial',
              alignment: 'left',
              border: [false, false, false, false],
            },
            {
              text: '',
              fontSize: 10,
              fontFamily: 'arial',
              alignment: 'center',
              border: [false, false, false, false],
            },
            {
              text: [{ text: 'Paid Quarter: ', bold: true }, options.quarter || ''],
              fontSize: 10,
              fontFamily: 'arial',
              alignment: 'right',
              border: [false, false, false, false],
            },
          ],
        ],
      },
      layout: 'noBorders',
    };
  };

  sortedDrugSeqs.forEach((drugSeqNum, index) => {
    const groupData = groupedByDrugSeq[drugSeqNum];
    let { units, claims, headerInfo } = groupData;

    if (units && units.length > 0) {
      units = units.sort((a, b) => {
        const ndcA = a.ndc ? String(a.ndc).trim() : '';
        const ndcB = b.ndc ? String(b.ndc).trim() : '';
        return ndcA.localeCompare(ndcB);
      });
    }

    if (claims && claims.length > 0) {
      claims = claims.sort((a, b) => {
        const claimIdA = a.claimId ? String(a.claimId).trim() : '';
        const claimIdB = b.claimId ? String(b.claimId).trim() : '';
        const numA = parseFloat(claimIdA);
        const numB = parseFloat(claimIdB);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB; 
        }
        return claimIdA.localeCompare(claimIdB);
      });
    }

    if (index === 0) {
      firstNdc = headerInfo.ndc;
      firstDrugName = headerInfo.drugName;
      allContent.push(createHeaderSection(firstNdc, firstDrugName, 1, sortedDrugSeqs.length));
    }

    if (index > 0) {
      allContent.push({
        text: '',
        pageBreak: 'before',
      });
      allContent.push(createHeaderSection(headerInfo.ndc, headerInfo.drugName, index + 1, sortedDrugSeqs.length));
    }
    
    if (units && units.length > 0) {
      allContent.push({
        style: 'tableExample',
        alignment: 'center',
        fontSize: 11,
        margin: [30.8, 10, 30.8, 5],
        text: [{ text: 'Unit Conversion', bold: true }],
      });

      allContent.push({
        alignment: 'center',
        fontSize: contentFont,
        margin: [80, 0, 80, 10],
        table: {
          widths: Array(unitColumnDefs.length).fill('*'),
          body: [
            getHeaderColumns(unitColumnDefs),
            ...getTableRows(units, unitColumnDefs),
          ],
        },
        layout: {
          hLineWidth: () => 0.1,
          vLineWidth: () => 0.1,
          // hLineColor: () => '#cccccc',
          // vLineColor: () => '#cccccc',
        },
        unbreakableRows: false,
        keepWithHeaderRows: 1,
      });
    }

    if (claims && claims.length > 0) {
      // allContent.push({
      //   style: 'tableExample',
      //   alignment: 'center',
      //   fontSize: 11,
      //   margin: [30.8, 15, 30.8, 5],
      //   text: [{ text: 'Adjusted Claims Data', bold: true }],
      // });

      allContent.push({
        alignment: 'center',
        fontSize: contentFont,
        margin: [15, 0, 15, 10],
        table: {
          widths: getColumnWidths(adjustedClaimsColumnDefs),
          body: [
            getHeaderColumns(adjustedClaimsColumnDefs),
            ...getTableRows(claims, adjustedClaimsColumnDefs),
            getGrandTotalRow(claims, adjustedClaimsColumnDefs),
          ],
        },
        layout: {
          hLineWidth: () => 0.1,
          vLineWidth: () => 0.1,
          // hLineColor: () => '#cccccc',
          // vLineColor: () => '#cccccc',
        },
        unbreakableRows: false,
        keepWithHeaderRows: 0,
      });

      allContent.push(getSelectionCriteriaHeader({
        selectedCriteria: options.selectedCriteria,
      }));
    }
  });

  allContent.push({
    text: '',
    margin: [0, 20, 0, 0],
  });

  // allContent.push(getFooter({
  //   selectedCriteria: options.selectedCriteria,
  // }));

  const docDefinition = {
    pageSize: options.pageSize || { width: 1920, height: 1080 },
    pageMargins,
    header: (currentPage, pageCount) => {
      return {
        text: `Page ${currentPage} of ${pageCount}`,
        fontSize: 10,
        alignment: 'right',
        margin: [30.8, 5, 30.8, 0],
      };
    },
    footer: (currentPage, pageCount) => {
      return {
        alignment: 'center',
        text: '2026 Conduent Business Services, LLC. All rights reserved. Conduent and Conduent Agile Star are trademarks of Conduent Business Services, LLC in the United States and/or other countries.',
        fontSize: 8,
        color: '#636363',
        fontFamily: 'Arial',
        margin: [30.8, 10, 30.8, 10],
      };
    },
    content: allContent,
    styles,
  };

  const pdf = pdfMake.createPdf(docDefinition);
  
  pdf.download(options.fileName || 'AdjustedClaims.pdf');
};

export default exportAdjustedClaimsPDF;
